/**
 * ClearChoice 2.0 Mobile Header Component
 * CLIENT COMPONENT – uses React hooks and browser events.
 */
'use client';

import { useState, useCallback } from 'react';

import Image from 'next/image';
import { Box, Button, Collapse, Drawer, IconButton, Stack, Typography, type SxProps } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';

import { App, NavigationGroup, NavigationLink } from '@/types/generated';
import { useAppContext } from '@/context';
import { clickContext } from '@/utils';
import LanguageToggle from '@/components/LanguageToggle/LanguageToggle';

import ContentTileCard from './ContentTileCard';
import { headerStyles } from './Header.styles';
import { CCNavLink } from './CCNavLink';

interface HeaderMobileProps {
	headerData?: App['header'] | null;
	logo?: App['logo'] | null;
	isDrawerOpen: boolean;
	onMenuClick: () => void;
	onClose: () => void;
	hideNavigation?: boolean;
	hideSchedule?: boolean;
	hideSearch?: boolean;
	showLanguageToggle?: boolean;
	logoHref?: string;
	/** When set, overrides the Contentful phone number so a DNI snippet can swap it. */
	phoneSwapNumber?: string;
	/** TTB-322: when true, render the "Call us" CTA as a ghost (secondary) button. */
	ghostPrimaryScheduleButton?: boolean;
}

type MenuLevel = 'main' | 'submenu';

const HeaderMobile = ({
	headerData,
	logo,
	isDrawerOpen,
	onMenuClick,
	onClose,
	hideNavigation = false,
	hideSchedule = false,
	hideSearch = false,
	showLanguageToggle = false,
	logoHref = '/',
	phoneSwapNumber,
	ghostPrimaryScheduleButton = false,
}: HeaderMobileProps) => {
	const { config } = useAppContext();
	const navMenuLinks = headerData?.navigationMenu?.headerMainNavLinksCollection;
	const [menuLevel, setMenuLevel] = useState<MenuLevel>('main');
	const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
	const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

	const phoneNumber = phoneSwapNumber ?? headerData?.phoneNumber?.linkText ?? headerData?.phoneNumber?.externalUrl;
	const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^\d+]/g, '')}` : undefined;
	const findCenterHref = headerData?.locationBarFindOfficeUrl ?? '/locations';
	const rawScheduleUrl = headerData?.schedulingButtonUrlMobile ?? headerData?.schedulingButtonUrl;
	const scheduleHref =
		rawScheduleUrl && !rawScheduleUrl.startsWith('tel:') ? rawScheduleUrl : '/schedule-a-consultation/';
	const mobileQuizLink = headerData?.mobileQuizLink as NavigationLink | undefined;

	const handleOpenSubmenu = useCallback(
		(index: number) => {
			const navItem = navMenuLinks?.items?.[index];
			if (!navItem) return;
			setActiveItemIndex(index);
			setMenuLevel('submenu');
			setExpandedGroupIds(new Set());
		},
		[navMenuLinks]
	);

	const handleBackToMain = useCallback(() => {
		setMenuLevel('main');
		setActiveItemIndex(null);
		setExpandedGroupIds(new Set());
	}, []);

	const handleClose = useCallback(() => {
		setMenuLevel('main');
		setActiveItemIndex(null);
		setExpandedGroupIds(new Set());
		onClose();
	}, [onClose]);

	const toggleGroup = useCallback((groupId: string) => {
		setExpandedGroupIds((prev) => {
			const next = new Set(prev);
			if (next.has(groupId)) {
				next.delete(groupId);
			} else {
				next.add(groupId);
			}
			return next;
		});
	}, []);

	const activeNavItem = activeItemIndex === null ? undefined : navMenuLinks?.items?.[activeItemIndex];
	const subNavGroups = (activeNavItem?.headerSubNavMenu?.headerSubNavGroupsCollection?.items ??
		[]) as NavigationGroup[];
	const subNavTiles = (activeNavItem?.headerSubNavMenu?.contentTilesCollection?.items ?? []).filter(Boolean);
	const categoryTitle = activeNavItem?.headerMainNavLink?.linkText ?? '';

	return (
		<>
			{/* ─── Mobile top bar ─── */}
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={headerStyles.mobileTopBar}>
				{/* Left: Hamburger + Logo */}
				<Stack direction="row" alignItems="center" gap="12px">
					{!hideNavigation && (
						<IconButton
							onClick={onMenuClick}
							aria-label="Open navigation menu"
							aria-expanded={isDrawerOpen}
							data-testid="mobile_menu_button"
							sx={headerStyles.mobileIconButton}
							disableRipple
						>
							<MenuIcon />
						</IconButton>
					)}

					{logo?.url ? (
						hideNavigation ? (
							<Box data-testid="header_logo_clearchoice2_mobile" sx={{ cursor: 'default' }}>
								<Box sx={headerStyles.mobileLogoBox}>
									<Image
										src={logo.url}
										alt={logo.description || 'ClearChoice'}
										width={101}
										height={32}
										style={{ width: '101px', height: '32px', objectFit: 'contain' }}
										priority
									/>
								</Box>
							</Box>
						) : (
							<CCNavLink
								href={logoHref}
								title="ClearChoice"
								analyticsContext={clickContext({ brand: config.name, type: 'logoClick' })}
								dataTestId="header_logo_clearchoice2_mobile"
							>
								<Box sx={headerStyles.mobileLogoBox}>
									<Image
										src={logo.url}
										alt={logo.description || 'ClearChoice'}
										width={101}
										height={32}
										style={{ width: '101px', height: '32px', objectFit: 'contain' }}
										priority
									/>
								</Box>
							</CCNavLink>
						)
					) : (
						<Box sx={{ width: '101px' }} />
					)}
				</Stack>

				{/* Right: Search icon + Call us + Language selector */}
				{!hideNavigation && (
					<Stack direction="row" alignItems="center" gap="8px">
						{!hideSearch && (
							<IconButton
								component="a"
								href={findCenterHref}
								aria-label="Find a center"
								data-testid="mobile_search_button"
								sx={headerStyles.mobileIconButton}
								disableRipple
							>
								<SearchIcon />
							</IconButton>
						)}

						{phoneHref && (
							<Button
								component="a"
								href={phoneHref}
								data-testid="mobile_call_us_button"
								variant={ghostPrimaryScheduleButton ? 'secondaryDefault' : undefined}
								sx={
									(ghostPrimaryScheduleButton
										? headerStyles.mobileCallUsButtonGhost
										: headerStyles.mobileCallUsButton) as SxProps
								}
								disableRipple
							>
								Call us
							</Button>
						)}

						{/* Language selector */}
						{showLanguageToggle && <LanguageToggle isLargeDevice={false} />}
					</Stack>
				)}
			</Stack>

			{/* ─── Navigation Drawer ─── */}
			{!hideNavigation && (
				<Drawer
					anchor="left"
					open={isDrawerOpen}
					onClose={handleClose}
					transitionDuration={300}
					PaperProps={{ sx: headerStyles.mobileDrawerPaper }}
					data-testid="mobile_navigation_drawer"
				>
					<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
						{/* Drawer logo + close */}
						<Box sx={headerStyles.mobileDrawerHeaderRow}>
							{logo?.url && (
								<Image
									src={logo.url}
									alt={logo.description || 'ClearChoice'}
									width={101}
									height={32}
									style={{ width: '101px', height: '32px' }}
								/>
							)}
							<IconButton
								onClick={handleClose}
								aria-label="Close navigation menu"
								data-testid="mobile_drawer_close"
								sx={headerStyles.mobileDrawerCloseButton}
							>
								<CloseIcon sx={{ fontSize: '28px' }} />
							</IconButton>
						</Box>

						{/* Scrollable content - nav items only */}
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								overscrollBehavior: 'contain',
								overflowY: 'auto',
								flex: 1,
								paddingTop: '28px',
								paddingBottom: '28px',
								paddingLeft: '20px',
								paddingRight: '20px',
							}}
						>
							{/* ══════════ MAIN MENU ══════════ */}
							<Box sx={{ display: menuLevel === 'main' ? 'block' : 'none' }}>
								{/* Always render main menu items to DOM for SEO, control visibility with CSS */}
								<>
									{navMenuLinks?.items?.map((item, index) => {
										if (!item || !item.headerMainNavLink) return null;
										const { headerMainNavLink, headerSubNavMenu } = item;
										const title = headerMainNavLink.linkText ?? '';
										const hasSubmenu =
											(headerSubNavMenu?.headerSubNavGroupsCollection?.items?.length ?? 0) > 0;
										const navHref = hasSubmenu
											? undefined
											: headerMainNavLink.pageData?.path ??
											  headerMainNavLink.externalUrl ??
											  undefined;

										if (navHref) {
											return (
												<CCNavLink key={index} href={navHref} title={title}>
													<Box
														sx={headerStyles.mobileNavItem}
														onClick={handleClose}
														data-testid={`mobile_nav_${title
															.toLowerCase()
															.replace(/\s+/g, '_')}`}
													>
														<Typography sx={headerStyles.mobileNavItemText}>
															{title}
														</Typography>
													</Box>
												</CCNavLink>
											);
										}

										return (
											<Box
												key={index}
												sx={headerStyles.mobileNavItem}
												onClick={() => handleOpenSubmenu(index)}
												data-testid={`mobile_nav_${title.toLowerCase().replace(/\s+/g, '_')}`}
											>
												<Typography sx={headerStyles.mobileNavItemText}>{title}</Typography>
												<ChevronRightIcon sx={headerStyles.mobileNavItemChevron} />
											</Box>
										);
									})}
								</>
							</Box>

							{/* ══════════ SUB-MENU ══════════ */}
							<Box sx={{ display: menuLevel === 'submenu' ? 'block' : 'none' }}>
								{/* Always render submenu items to DOM for SEO, control visibility with CSS */}
								{activeNavItem && (
									<>
										{/* Back row — keyboard-accessible via role="button" */}
										<Box
											sx={headerStyles.mobileSubMenuBackRow}
											onClick={handleBackToMain}
											role="button"
											tabIndex={0}
											onKeyDown={(e: React.KeyboardEvent) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													handleBackToMain();
												}
											}}
										>
											<ArrowBackIcon sx={headerStyles.mobileSubMenuBackIcon} />
											<Typography sx={headerStyles.mobileSubMenuBackLabel}>
												{categoryTitle}
											</Typography>
										</Box>

										{/* Navigation groups */}
										{subNavGroups.map((group, groupIndex) => {
											if (!group) return null;
											// Use index as fallback so groups without a sys.id never share a key
											const groupId = group.sys?.id ?? `group-${groupIndex}`;
											const groupTitle = group.groupLink?.linkText ?? '';
											const groupHref =
												group.groupLink?.pageData?.path ?? group.groupLink?.externalUrl;
											const subLinks = (group.linksCollection?.items ?? []).filter(Boolean);
											const isExpanded = expandedGroupIds.has(groupId);

											if (subLinks.length > 0) {
												return (
													<Box key={groupId} sx={headerStyles.mobileGroupContainer}>
														{/* Accordion toggle — keyboard-accessible via role="button" */}
														<Box
															sx={headerStyles.mobileGroupHeader}
															onClick={() => toggleGroup(groupId)}
															aria-expanded={isExpanded}
															role="button"
															tabIndex={0}
															onKeyDown={(e: React.KeyboardEvent) => {
																if (e.key === 'Enter' || e.key === ' ') {
																	e.preventDefault();
																	toggleGroup(groupId);
																}
															}}
															data-testid={`mobile_group_${groupTitle
																.toLowerCase()
																.replace(/\s+/g, '_')}`}
														>
															<Box
																component="span"
																sx={headerStyles.mobileGroupHeaderText}
															>
																{groupTitle}
															</Box>
															{isExpanded ? (
																<ExpandLessIcon
																	sx={headerStyles.mobileGroupToggleIcon}
																/>
															) : (
																<ExpandMoreIcon
																	sx={headerStyles.mobileGroupToggleIcon}
																/>
															)}
														</Box>
														<Collapse in={isExpanded} timeout={300}>
															{subLinks.map((link) => {
																const linkHref =
																	link?.pageData?.path ?? link?.externalUrl ?? '#';
																return (
																	<CCNavLink
																		key={link?.sys?.id}
																		href={linkHref}
																		title={link?.linkText ?? ''}
																	>
																		<Box
																			sx={headerStyles.mobileGroupSubItem}
																			onClick={handleClose}
																			data-testid={`mobile_sub_${(
																				link?.linkText ?? ''
																			)
																				.toLowerCase()
																				.replace(/\s+/g, '_')}`}
																		>
																			<Box
																				component="span"
																				sx={headerStyles.mobileGroupSubItemText}
																			>
																				{link?.linkText}
																			</Box>
																		</Box>
																	</CCNavLink>
																);
															})}
														</Collapse>
													</Box>
												);
											}

											// Plain link (group with no sub-items)
											return (
												<CCNavLink key={groupId} href={groupHref ?? '#'} title={groupTitle}>
													<Box
														sx={headerStyles.mobileNavItem}
														onClick={handleClose}
														data-testid={`mobile_group_link_${groupTitle
															.toLowerCase()
															.replace(/\s+/g, '_')}`}
													>
														<Box component="span" sx={headerStyles.mobileGroupHeaderText}>
															{groupTitle}
														</Box>
													</Box>
												</CCNavLink>
											);
										})}

										{/* Content tiles */}
										{subNavTiles.length > 0 && (
											<Box sx={headerStyles.mobileTilesSection}>
												{subNavTiles.map((tile) => {
													if (!tile) return null;
													return (
														<ContentTileCard
															key={tile.sys?.id}
															tile={tile}
															onClick={handleClose}
														/>
													);
												})}
											</Box>
										)}
									</>
								)}
							</Box>

							{/* Actions section - sinks to the foot of the drawer, falling back to normal flow when the nav is tall enough to scroll */}
							<Box sx={{ display: menuLevel === 'main' ? 'block' : 'none', marginTop: 'auto' }}>
								<Box sx={headerStyles.mobileDrawerActionsBottom}>
									{/* Find a center */}
									<CCNavLink
										href={findCenterHref}
										title="Find a center"
										analyticsContext={clickContext({ brand: config.name, type: 'findCenter' })}
										dataTestId="mobile_find_center"
									>
										<Box sx={headerStyles.mobileActionItem} onClick={handleClose}>
											<LocationOnOutlinedIcon sx={headerStyles.mobileActionIcon} />
											<Typography sx={headerStyles.mobileActionItemText}>
												Find a center
											</Typography>
										</Box>
									</CCNavLink>
									{/* Schedule now */}
									{!hideSchedule && (
										<CCNavLink
											href={scheduleHref}
											title="Schedule now"
											analyticsContext={clickContext({
												brand: config.name,
												type: 'scheduleClick',
											})}
											dataTestId="mobile_schedule_now"
										>
											<Box sx={headerStyles.mobileActionItem} onClick={handleClose}>
												<EventNoteOutlinedIcon sx={headerStyles.mobileActionIcon} />
												<Typography sx={headerStyles.mobileActionItemText}>
													Schedule now
												</Typography>
											</Box>
										</CCNavLink>
									)}
									{/* Call us */}
									{phoneHref && (
										<CCNavLink
											href={phoneHref}
											title="Call us"
											analyticsContext={clickContext({ brand: config.name, type: 'callUs' })}
											dataTestId="mobile_call_us"
										>
											<Box sx={headerStyles.mobileActionItem} onClick={handleClose}>
												<CallOutlinedIcon sx={headerStyles.mobileActionIcon} />
												<Typography sx={headerStyles.mobileActionItemText}>Call us</Typography>
											</Box>
										</CCNavLink>
									)}
									{/* Quiz link */}
									{mobileQuizLink && (
										<CCNavLink
											href={mobileQuizLink.pageData?.path ?? mobileQuizLink.externalUrl ?? '#'}
											title={mobileQuizLink.linkText ?? ''}
											analyticsContext={clickContext({ brand: config.name, type: 'quizClick' })}
											dataTestId="mobile_quiz_link"
										>
											<Box sx={headerStyles.mobileActionItem} onClick={handleClose}>
												{mobileQuizLink.linkIcon?.url ? (
													<Box
														component="img"
														src={mobileQuizLink.linkIcon.url}
														alt={mobileQuizLink.linkText ?? 'Quiz'}
														sx={headerStyles.mobileActionIconImage}
													/>
												) : null}
												<Typography sx={headerStyles.mobileActionItemText}>
													{mobileQuizLink.linkText}
												</Typography>
											</Box>
										</CCNavLink>
									)}
								</Box>
							</Box>
						</Box>
					</Box>
				</Drawer>
			)}
		</>
	);
};

export default HeaderMobile;
